import {
  Currency,
  CURRENCIES_WITHOUT_SYMBOL,
  currencyToCode,
  parseCurrency,
} from './currency';

/**
 * Display style for currency symbols
 */
export enum CurrencyDisplayStyle {
  /** e.g., "$1,234.56" */
  SYMBOL = 'symbol',
  /** e.g., "$1,234.56" (narrower version) */
  NARROW_SYMBOL = 'narrowSymbol',
  /** e.g., "USD 1,234.56" */
  CODE = 'code',
  /** e.g., "USD $123.23" (code prefix + symbol, no grouping) */
  CODE_AND_SYMBOL = 'codeAndSymbol',
  /** e.g., "1,234.56 US dollars" */
  NAME = 'name',
  /** e.g., "1,234.56" (no currency indicator) */
  NONE = 'none',
}

/**
 * Type for currency display style.
 *
 * Accepts both enum values (preferred) and string literals (for backwards compatibility).
 * Prefer using `CurrencyDisplayStyle` enum values for better type safety and IDE support.
 *
 * @example
 * ```ts
 * // Preferred: use enum
 * formatCurrency(Currency.USD, {
 *   currencyDisplay: CurrencyDisplayStyle.CODE
 * });
 *
 * // Backwards compatible: string literal still works
 * formatCurrency(Currency.USD, {
 *   currencyDisplay: 'code'
 * });
 * ```
 */
export type CurrencyDisplayStyleType =
  | CurrencyDisplayStyle
  | 'symbol'
  | 'narrowSymbol'
  | 'code'
  | 'codeAndSymbol'
  | 'name'
  | 'none';

/**
 * Valid currency display values for Intl.NumberFormat
 */
type IntlCurrencyDisplay = 'symbol' | 'narrowSymbol' | 'code' | 'name';

/**
 * Type guard to check if a string is a valid Intl.NumberFormat currencyDisplay value
 */
function isValidIntlCurrencyDisplay(
  value: string
): value is IntlCurrencyDisplay {
  return (
    value === 'symbol' ||
    value === 'narrowSymbol' ||
    value === 'code' ||
    value === 'name'
  );
}

/**
 * Number formatting style
 */
export type NumberFormatStyle = 'decimal' | 'currency' | 'percent' | 'unit';

/**
 * Options for formatting currency values
 */
export interface CurrencyFormatOptions {
  /** BCP 47 language tag (e.g., 'en-US', 'de-DE'). Defaults to 'en-US' */
  readonly locale?: string;

  /** Minimum number of integer digits to use. Values range from 1 to 21 */
  readonly minimumIntegerDigits?: number;

  /** Minimum number of fraction digits to use. Values range from 0 to 20 */
  readonly minimumFractionDigits?: number;

  /** Maximum number of fraction digits to use. Values range from 0 to 20 */
  readonly maximumFractionDigits?: number;

  /** Minimum number of significant digits to use. Values range from 1 to 21 */
  readonly minimumSignificantDigits?: number;

  /** Maximum number of significant digits to use. Values range from 1 to 21 */
  readonly maximumSignificantDigits?: number;

  /** How to display the currency. Defaults to CurrencyDisplayStyle.SYMBOL */
  readonly currencyDisplay?: CurrencyDisplayStyleType;

  /** The formatting style to use. Defaults to 'currency' */
  readonly style?: NumberFormatStyle;

  /** Whether to use grouping separators (e.g., thousands separators). Defaults to true */
  readonly useGrouping?: boolean;
}

/**
 * A function that formats numbers as currency strings
 */
export type CurrencyFormatter = (value: number) => string;

/**
 * Create a reusable currency formatter function using Intl.NumberFormat.
 *
 * Returns a formatter function that can be reused for multiple values, which is
 * more efficient than creating a new formatter for each value. The returned
 * function formats numbers as currency strings according to the provided options.
 *
 * @param currency - The Currency enum value, currency code string (e.g., 'USD', 'CAD'),
 *                    or GraphQL currency string (e.g., 'CURRENCY_USD', 'CURRENCY_CAD') to format with.
 *                    Accepts the most permissive form for client correctness.
 * @param options - Formatting options (locale, display style, fraction digits, etc.)
 * @returns A function that formats numbers as currency strings
 *
 * @example
 * ```ts
 * // Basic usage with USD enum
 * const formatUSD = formatCurrency(Currency.USD);
 * formatUSD(1234.56); // "$1,234.56"
 * formatUSD(99.99);   // "$99.99"
 *
 * // Usage with currency code string
 * const formatCAD = formatCurrency('CAD');
 * formatCAD(1234.56); // "CA$1,234.56"
 *
 * // Usage with GraphQL currency string
 * const formatEUR = formatCurrency('CURRENCY_EUR');
 * formatEUR(1234.56); // "€1,234.56"
 *
 * // Custom locale and display
 * const formatEUR = formatCurrency(Currency.EUR, {
 *   locale: 'de-DE',
 *   currencyDisplay: CurrencyDisplayStyle.CODE
 * });
 * formatEUR(1234.56); // "1.234,56 EUR"
 *
 * // Narrow symbol for compact display
 * const formatCompact = formatCurrency(Currency.USD, {
 *   currencyDisplay: CurrencyDisplayStyle.NARROW_SYMBOL
 * });
 * formatCompact(99.99); // "$99.99"
 *
 * // Code and symbol format (e.g., "USD $123.23")
 * const formatWithCode = formatCurrency(Currency.USD, {
 *   currencyDisplay: CurrencyDisplayStyle.CODE_AND_SYMBOL
 * });
 * formatWithCode(123.23); // "USD $123.23"
 *
 * // Format multiple values efficiently
 * const formatter = formatCurrency(Currency.USD);
 * const amounts = [100, 200, 300];
 * const formatted = amounts.map(formatter);
 * ```
 */
export function formatCurrency(
  currency: Currency | string,
  options: CurrencyFormatOptions = {}
): CurrencyFormatter {
  // Parse currency string to Currency enum if needed
  // Handles both ISO currency codes ('USD', 'CAD') and GraphQL currency strings ('CURRENCY_USD', 'CURRENCY_CAD')
  let domainCurrency: Currency;

  if (typeof currency === 'string') {
    // Handle GraphQL currency strings (e.g., 'CURRENCY_USD' -> 'USD')
    const normalizedCurrency = currency.startsWith('CURRENCY_')
      ? currency.replace(/^CURRENCY_/, '')
      : currency;

    domainCurrency = parseCurrency(normalizedCurrency);
  } else {
    domainCurrency = currency;
  }

  const {
    locale = 'en-US',
    minimumIntegerDigits,
    minimumFractionDigits,
    maximumFractionDigits,
    minimumSignificantDigits,
    maximumSignificantDigits,
    currencyDisplay = CurrencyDisplayStyle.SYMBOL,
    style = 'currency',
    useGrouping = true,
  } = options;

  // Normalize enum to string for Intl.NumberFormat compatibility
  // Enum values are strings, so we can use them directly
  const displayStyle: string = String(currencyDisplay);

  // Normalize locale format (e.g., 'en_US' -> 'en-US')
  const normalizedLocale = locale.replace('_', '-');

  // Get the ISO currency code
  const currencyCode = currencyToCode(domainCurrency);

  // Handle special 'codeAndSymbol' display mode (e.g., "USD $123.23")
  if (displayStyle === 'codeAndSymbol') {
    const numberFormat = new Intl.NumberFormat(normalizedLocale, {
      currency: currencyCode,
      style,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: minimumFractionDigits ?? 2,
      maximumFractionDigits: maximumFractionDigits ?? 2,
      useGrouping: false, // No grouping for codeAndSymbol format
    });

    return (value: number) => {
      const parts = numberFormat.formatToParts(value);
      // Find the currency part (not the first part, as negative amounts have minusSign first)
      const currencyPart = parts.find(part => part.type === 'currency');
      const symbol = currencyPart?.value ?? '';

      // Handle negative amounts: format with absolute value and add negative sign if needed
      const isNegative = value < 0;
      const absoluteAmount = Math.abs(value);
      const fractionDigits =
        maximumFractionDigits ?? minimumFractionDigits ?? 2;
      const formattedAmount = absoluteAmount.toFixed(fractionDigits);

      // Omit symbol when it equals the code to avoid duplication (e.g. "AED AED123.23").
      // AED and similar currencies (SAR, QAR, CHF, etc.) have official symbols, but many are
      // not yet in Unicode. Intl.NumberFormat uses ICU data tied to Unicode; when no symbol
      // exists, it falls back to the currency code. We skip the symbol in that case.
      // Also omit symbol for AMD, KHR, BDT, KGS per product requirement.
      const effectiveSymbol =
        symbol === currencyCode || CURRENCIES_WITHOUT_SYMBOL.has(domainCurrency)
          ? ''
          : symbol;

      return `${currencyCode} ${isNegative ? '-' : ''}${effectiveSymbol}${formattedAmount}`;
    };
  }

  // Type-safe currency display value for Intl.NumberFormat
  // When displayStyle is 'none', we use 'symbol' as a placeholder (will be handled separately)
  let intlCurrencyDisplay: IntlCurrencyDisplay = 'symbol';

  if (displayStyle !== 'none' && isValidIntlCurrencyDisplay(displayStyle)) {
    intlCurrencyDisplay = displayStyle;
  }

  const numberFormat = new Intl.NumberFormat(normalizedLocale, {
    currency: currencyCode,
    style,
    currencyDisplay: intlCurrencyDisplay,
    minimumIntegerDigits,
    minimumFractionDigits,
    maximumFractionDigits,
    minimumSignificantDigits,
    maximumSignificantDigits,
    useGrouping,
  });

  // Handle special 'none' display mode
  if (displayStyle === 'none') {
    return (value: number) => {
      return value.toLocaleString(normalizedLocale, {
        ...numberFormat.resolvedOptions(),
        style: 'decimal',
      });
    };
  }

  // For AMD, KHR, BDT, KGS: omit symbol, show number only
  if (
    CURRENCIES_WITHOUT_SYMBOL.has(domainCurrency) &&
    (intlCurrencyDisplay === 'symbol' || intlCurrencyDisplay === 'narrowSymbol')
  ) {
    return (value: number) => {
      const parts = numberFormat.formatToParts(value);

      return parts
        .filter(p => p.type !== 'currency')
        .map(p => p.value)
        .join('')
        .trim();
    };
  }

  return numberFormat.format;
}

/**
 * Format a single amount as currency with narrow symbol display.
 *
 * Convenience function for one-off formatting. Uses narrow symbol display style
 * by default. For formatting multiple values with the same currency, prefer using
 * `formatCurrency()` and reusing the returned formatter for better performance.
 *
 * @param currency - The Currency enum value to format with
 * @param amount - The numeric amount to format
 * @param locale - Optional BCP 47 locale string (e.g., 'en-US', 'de-DE'). Defaults to 'en-US'
 * @returns Formatted currency string with narrow symbol (e.g., "$1,234.56", "€1,234.56")
 *
 * @example
 * ```ts
 * formatCurrencyAmount(Currency.USD, 1234.56);        // "$1,234.56"
 * formatCurrencyAmount(Currency.EUR, 1234.56);        // "€1,234.56"
 * formatCurrencyAmount(Currency.EUR, 1234.56, 'de-DE'); // "1.234,56 €"
 * formatCurrencyAmount(Currency.GBP, 1000);           // "£1,000.00"
 * formatCurrencyAmount(Currency.USD, -1234.56);       // "-$1,234.56"
 * ```
 */
export function formatCurrencyAmount(
  currency: Currency,
  amount: number,
  locale?: string
): string {
  const formatter = formatCurrency(currency, {
    currencyDisplay: CurrencyDisplayStyle.NARROW_SYMBOL,
    ...(locale && { locale }),
  });

  return formatter(amount);
}

/**
 * Format a single amount as currency with symbol only (no country prefix).
 *
 * Similar to `formatCurrencyAmount` but removes any country prefixes from the symbol.
 * For example, in en-CA locale, USD normally displays as "US $123" - this function
 * returns just "$123". Useful for cleaner display in multi-currency contexts.
 *
 * @param currency - The Currency enum value to format with
 * @param amount - The numeric amount to format
 * @param locale - Optional BCP 47 locale string (e.g., 'en-US', 'de-DE'). Defaults to 'en-US'
 * @returns Formatted currency string with symbol only (e.g., "$1,234.56", "€1,234.56")
 *
 * @example
 * ```ts
 * formatCurrencyWithSymbolOnly(Currency.USD, 123.45, 'en-CA');  // "$123.45" (not "US $123.45")
 * formatCurrencyWithSymbolOnly(Currency.CAD, 123.45, 'en-US');  // "$123.45" (not "CA$123.45")
 * formatCurrencyWithSymbolOnly(Currency.EUR, 1234.56);          // "€1,234.56"
 * formatCurrencyWithSymbolOnly(Currency.USD, -123.45, 'en-CA'); // "-$123.45"
 * ```
 */
export function formatCurrencyWithSymbolOnly(
  currency: Currency,
  amount: number,
  locale?: string
): string {
  let result = formatCurrencyAmount(currency, amount, locale);

  // Remove country prefixes for currencies when displayed in locales where they might be ambiguous
  // For example, in en-CA locale, USD shows as "US $123" - we want just "$123"
  // Use {2,} to avoid stripping single letters that are part of symbols (e.g. "Ft" for Hungarian Forint)
  result = result.replace(/^[A-Z]{2,}\s*/, '');
  
  return result;
}

/**
 * Format an amount with currency code display (e.g., "USD 1,234.56").
 *
 * Useful for contexts where the full currency code is preferred over symbols,
 * such as financial reports or transaction logs.
 *
 * @param currency - The Currency enum value or currency code string
 * @param amount - The numeric amount to format
 * @param locale - Optional BCP 47 locale string. Defaults to 'en-US'
 * @returns Formatted currency string
 *
 * @example
 * ```ts
 * formatCurrencyWithCode(Currency.USD, 1234.56); // "USD 1,234.56"
 * formatCurrencyWithCode(Currency.EUR, 1234.56); // "EUR 1,234.56"
 * ```
 */
export function formatCurrencyWithCode(
  currency: Currency | string,
  amount: number,
  locale?: string
): string {
  const formatter = formatCurrency(currency, {
    currencyDisplay: CurrencyDisplayStyle.CODE,
    ...(locale && { locale }),
  });

  return formatter(amount);
}

/**
 * Format an amount with code and symbol display (e.g., "USD $123.23").
 *
 * Combines the currency code prefix with the narrow symbol for maximum clarity.
 * Useful for invoices, charges, and financial summaries.
 *
 * @param currency - The Currency enum value or currency code string
 * @param amount - The numeric amount to format
 * @param locale - Optional BCP 47 locale string. Defaults to 'en-US'
 * @returns Formatted currency string
 *
 * @example
 * ```ts
 * formatCurrencyCodeAndSymbol(Currency.USD, 1234.56); // "USD $1234.56"
 * formatCurrencyCodeAndSymbol(Currency.EUR, 1234.56); // "EUR €1234.56"
 * ```
 */
export function formatCurrencyCodeAndSymbol(
  currency: Currency | string,
  amount: number,
  locale?: string
): string {
  const formatter = formatCurrency(currency, {
    currencyDisplay: CurrencyDisplayStyle.CODE_AND_SYMBOL,
    ...(locale && { locale }),
  });

  return formatter(amount);
}

/**
 * Format an amount without symbol display (e.g., "1,234.56").
 *
 * Formats the number with locale-appropriate grouping but without any currency
 * indicator. Useful when the currency is displayed separately or in table columns.
 *
 * @param currency - The Currency enum value or currency code string
 * @param amount - The numeric amount to format
 * @param locale - Optional BCP 47 locale string. Defaults to 'en-US'
 * @returns Formatted currency string
 *
 * @example
 * ```ts
 * formatCurrencyNoSymbol(Currency.USD, 1234.56); // "1,234.56"
 * formatCurrencyNoSymbol(Currency.JPY, 1234); // "1,234"
 * ```
 */
export function formatCurrencyNoSymbol(
  currency: Currency | string,
  amount: number,
  locale?: string
): string {
  const formatter = formatCurrency(currency, {
    currencyDisplay: CurrencyDisplayStyle.NONE,
    ...(locale && { locale }),
  });

  return formatter(amount);
}

/**
 * Format an amount without symbol and without grouping (e.g., "1234.56").
 *
 * Formats the number with currency-appropriate decimal places but without any
 * currency indicator or thousands separators. Useful for input fields, calculations,
 * and data exports where plain numbers are needed.
 *
 * @param currency - The Currency enum value or currency code string
 * @param amount - The numeric amount to format
 * @param locale - Optional BCP 47 locale string. Defaults to 'en-US'
 * @returns Formatted currency string
 *
 * @example
 * ```ts
 * formatCurrencyPlain(Currency.USD, 1234.56); // "1234.56"
 * formatCurrencyPlain(Currency.JPY, 1234); // "1234"
 * ```
 */
export function formatCurrencyPlain(
  currency: Currency | string,
  amount: number,
  locale?: string
): string {
  const formatter = formatCurrency(currency, {
    currencyDisplay: CurrencyDisplayStyle.NONE,
    useGrouping: false,
    ...(locale && { locale }),
  });

  return formatter(amount);
}
